import { Card } from './Card';

/**
 * Standardized Skeleton Loader Components
 * Consistent loading states across the application
 */

// Base Skeleton Component
export const Skeleton = ({ className = '', width, height }: { 
  className?: string; 
  width?: string | number; 
  height?: string | number;
}) => {
  const style = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
  };

  return (
    <div 
      className={`skeleton bg-gray-200 rounded animate-pulse ${className}`}
      style={style}
      aria-hidden="true"
    />
  );
};

// Card Skeleton
export const SkeletonCard = () => {
  return (
    <Card padding="lg" className="animate-pulse">
      <div className="flex items-start gap-4 mb-4">
        <Skeleton className="w-16 h-16 rounded-full" />
        <div className="flex-1 space-y-3">
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      </div>
      <div className="space-y-2 mb-4">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-4 w-4/6" />
      </div>
      <div className="flex gap-2">
        <Skeleton className="h-10 w-32 rounded-lg" />
        <Skeleton className="h-10 w-24 rounded-lg" />
      </div>
    </Card>
  );
};

// Job Card Skeleton
export const SkeletonJobCard = () => {
  return (
    <Card padding="md" className="animate-pulse">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 space-y-2">
          <Skeleton className="h-6 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </div>
        <Skeleton className="h-6 w-20 rounded-full" />
      </div>
      <div className="space-y-2 mb-4">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Skeleton className="h-12 w-full rounded-lg" />
        <Skeleton className="h-12 w-full rounded-lg" />
      </div>
      <Skeleton className="h-10 w-full rounded-lg" />
    </Card>
  );
};

// Provider Card Skeleton
export const SkeletonProviderCard = () => {
  return (
    <Card padding="lg" className="animate-pulse">
      <div className="flex items-start gap-4 mb-4">
        <Skeleton className="w-16 h-16 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-5 w-3/4" />
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-20" />
          </div>
        </div>
      </div>
      <div className="flex gap-2 mb-4">
        <Skeleton className="h-6 w-20 rounded-full" />
        <Skeleton className="h-6 w-24 rounded-full" />
      </div>
      <div className="space-y-2 mb-4">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
      </div>
      <div className="flex gap-2">
        <Skeleton className="h-10 flex-1 rounded-lg" />
        <Skeleton className="h-10 w-24 rounded-lg" />
      </div>
    </Card>
  );
};

// Table Row Skeleton
export const SkeletonTableRow = () => {
  return (
    <div className="flex items-center gap-4 p-4 border-b border-gray-100 animate-pulse">
      <Skeleton className="w-10 h-10 rounded" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-3 w-1/4" />
      </div>
      <Skeleton className="h-8 w-20 rounded-lg" />
    </div>
  );
};

// Table Skeleton
export const SkeletonTable = ({ rows = 5 }: { rows?: number }) => {
  return (
    <Card padding="none">
      <div className="p-4 border-b border-gray-200 animate-pulse">
        <Skeleton className="h-5 w-32" />
      </div>
      <div>
        {Array.from({ length: rows }).map((_, i) => (
          <SkeletonTableRow key={i} />
        ))}
      </div>
    </Card>
  );
};

// List Item Skeleton
export const SkeletonListItem = () => {
  return (
    <div className="flex items-center gap-3 p-4 animate-pulse">
      <Skeleton className="w-12 h-12 rounded-full" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-3 w-1/2" />
      </div>
      <Skeleton className="h-8 w-16 rounded-lg" />
    </div>
  );
};

// List Skeleton
export const SkeletonList = ({ items = 5 }: { items?: number }) => {
  return (
    <div className="space-y-2">
      {Array.from({ length: items }).map((_, i) => (
        <SkeletonListItem key={i} />
      ))}
    </div>
  );
};

// Dashboard Stats Skeleton
export const SkeletonStats = () => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
      {Array.from({ length: 4 }).map((_, i) => (
        <Card key={i} padding="lg" className="animate-pulse">
          <div className="flex items-center justify-between mb-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="w-8 h-8 rounded-lg" />
          </div>
          <Skeleton className="h-8 w-20 mb-1" />
          <Skeleton className="h-3 w-16" />
        </Card>
      ))}
    </div>
  );
};

// Page Header Skeleton
export const SkeletonPageHeader = () => {
  return (
    <div className="mb-6 sm:mb-8 animate-pulse">
      <Skeleton className="h-8 w-64 mb-2" />
      <Skeleton className="h-4 w-96" />
    </div>
  );
};

// Full Page Skeleton
export const SkeletonPage = () => {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <SkeletonPageHeader />
      <SkeletonStats />
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <SkeletonCard />
          <SkeletonCard />
        </div>
        <div className="space-y-6">
          <SkeletonCard />
        </div>
      </div>
    </div>
  );
};

// Text Skeleton
export const SkeletonText = ({ lines = 3 }: { lines?: number }) => {
  return (
    <div className="space-y-2 animate-pulse">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton 
          key={i} 
          className="h-4" 
          width={i === lines - 1 ? '60%' : '100%'} 
        />
      ))}
    </div>
  );
};

// Avatar Skeleton
export const SkeletonAvatar = ({ size = 40 }: { size?: number }) => {
  return (
    <Skeleton 
      className="rounded-full" 
      width={size} 
      height={size} 
    />
  );
};

// Image Skeleton
export const SkeletonImage = ({ 
  aspectRatio = '16/9',
  className = '' 
}: { 
  aspectRatio?: string;
  className?: string;
}) => {
  return (
    <div 
      className={`skeleton bg-gray-200 rounded-lg animate-pulse ${className}`}
      style={{ aspectRatio }}
      aria-hidden="true"
    />
  );
};
